<template>
  <!-- 画像トリミング・圧縮モーダル -->
  <div :id="modalId" class="c-modal c-modal--image-cropper">
    <div class="c-modal__bg"></div>
    <div class="c-modal__content c-modal__content--large">
      <div class="c-modal__close" @click="cancelCrop">
        <div class="c-icon c-icon--white u-wh14">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
          >
            <use xlink:href="/assets/img/sprite.svg#close" />
          </svg>
        </div>
      </div>
      <div class="c-modal__inner">
        <div class="c-modal__title c-modal__title--bold">{{ modalTitle }}</div>
        <div class="c-modal__description" v-html="modalDescription"></div>
        <div class="c-modal__data">
          <div class="c-image-cropper">
            <div class="c-image-cropper__canvas" :style="{ width: containerWidth + 'px', height: containerHeight + 'px' }">
              <vue-cropper
                v-if="imageSource"
                ref="cropper"
                :src="imageSource"
                :aspect-ratio="aspectRatio"
                :view-mode="2"
                :auto-crop-area="1"
                :background="true"
                :guides="true"
                :center="true"
                :highlight="true"
                :crop-box-movable="true"
                :crop-box-resizable="true"
                :toggle-drag-mode-on-dblclick="false"
                :min-container-width="containerWidth"
                :min-container-height="containerHeight"
                @ready="onCropperReady"
              />
            </div>
            <div v-if="isProcessing" class="c-image-cropper__processing">
              圧縮処理中...
            </div>
            <div
              v-if="compressionError"
              class="c-image-cropper__error"
            >
              {{ compressionError }}
            </div>
          </div>
        </div>
        <div class="c-modal__btns">
          <button
            class="c-btn c-btn--modal c-btn--gray u-w140 u-h34 u-fs14"
            type="button"
            @click="cancelCrop"
          >
            <p class="c-btn__text">キャンセル</p>
          </button>
          <button
            class="c-btn c-btn--modal c-btn--blue u-w140 u-h34 u-fs14"
            :disabled="isProcessing"
            type="button"
            @click="confirmCrop"
          >
            <p class="c-btn__text">確定</p>
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- /画像トリミング・圧縮モーダル -->
</template>

<script>
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';

export default {
  components: {
    VueCropper,
  },
  props: {
    // モーダルのID
    modalId: {
      type: String,
      default: 'imageCropperModal',
    },
    // モーダルのタイトル
    modalTitle: {
      type: String,
      default: '通知アイコン最適化',
    },
    // モーダルの説明文（HTMLを含む場合はv-htmlで表示）
    modalDescription: {
      type: String,
      default: '画像が正方形でないか、8KBを超えています。<br>アイコンに使用する範囲を選択してください。<br>(192×192px、8KB以下に自動変換されます)',
    },
    // トリミングのアスペクト比（1:1 = 1, 1:2 = 0.5, 2:1 = 2）
    aspectRatio: {
      type: Number,
      default: 1,
    },
    // 出力画像の幅（px）
    outputWidth: {
      type: Number,
      default: 192,
    },
    // 出力画像の高さ（px）
    outputHeight: {
      type: Number,
      default: 192,
    },
    // 最大ファイルサイズ（バイト）
    maxFileSize: {
      type: Number,
      default: 8192, // 8KB
    },
    // 優先する画像形式（'jpeg' or 'png'）
    preferredFormat: {
      type: String,
      default: 'jpeg',
      validator: (value) => ['jpeg', 'png'].includes(value),
    },
  },
  emits: ["cancelled","cropped"],
  data() {
    return {
      imageSource: null,
      originalFileSize: 0,
      compressedFileSize: 0,
      isProcessing: false,
      compressionError: null,
      cropperReady: false,
      containerWidth: 200, // コンテナの幅（動的に調整）
      containerHeight: 200, // コンテナの高さ（動的に調整）
    };
  },
  methods: {
    /**
     * モーダルを表示
     * @param {File} file - トリミングする画像ファイル
     */
    showModal(file) {
      this.originalFileSize = file.size;
      this.compressedFileSize = 0;
      this.compressionError = null;
      this.cropperReady = false;

      // FileReaderで画像を読み込み
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        () => {
          // 画像のサイズを取得してコンテナのサイズを計算
          const img = new Image();
          img.onload = () => {
            const imageWidth = img.width;
            const imageHeight = img.height;
            const aspectRatio = imageHeight / imageWidth;
            const targetAspectRatio = this.outputHeight / this.outputWidth;

            let containerWidth, containerHeight;

            // 正方形の出力（通知アイコン用）の場合は以前のシンプルなロジック
            if (this.aspectRatio === 1) {
              // 縦長画像の場合、コンテナの高さを調整（最大ブラウザ高さの80%）
              if (aspectRatio > 1) {
                // 幅200pxに対して、アスペクト比に応じた高さを計算
                const calculatedHeight = 200 * aspectRatio;
                const maxHeight = window.innerHeight * 0.8;
                containerWidth = 200;
                containerHeight = Math.min(calculatedHeight, maxHeight);
              } else {
                // 正方形または横長の場合は200px
                containerWidth = 200;
                containerHeight = 200;
              }
            }
            // 非正方形の出力（大型画像用など）の場合は新しいロジック
            else {
              // モーダル内に収まる最大サイズを計算
              const maxModalWidth = Math.min(window.innerWidth * 0.7, 550);
              const maxModalHeight = Math.min(window.innerHeight * 0.6, 400);

              // 縦長画像の場合（アスペクト比 > 1）
              if (aspectRatio > 1) {
                // 高さを基準にスケーリング
                if (imageHeight > maxModalHeight) {
                  containerHeight = maxModalHeight;
                  containerWidth = containerHeight / aspectRatio;
                } else {
                  // 画像が小さい場合は、適度なサイズで表示
                  containerHeight = Math.min(imageHeight, 300);
                  containerWidth = containerHeight / aspectRatio;
                }
              }
              // 横長画像の場合（アスペクト比 < 1）
              else if (aspectRatio < 1) {
                // 幅を基準にスケーリング
                if (imageWidth > maxModalWidth) {
                  containerWidth = maxModalWidth;
                  containerHeight = containerWidth * aspectRatio;
                } else {
                  // 画像が小さい場合は、適度なサイズで表示（目標アスペクト比を考慮）
                  containerWidth = Math.max(Math.min(imageWidth, maxModalWidth), 400);
                  containerHeight = containerWidth * targetAspectRatio;
                }
              }
              // 正方形の画像の場合
              else {
                const maxSize = Math.min(maxModalWidth, maxModalHeight);
                containerWidth = Math.min(imageWidth, maxSize, 300);
                containerHeight = containerWidth;
              }
            }

            // 最終的なサイズを設定
            this.containerWidth = Math.round(containerWidth);
            this.containerHeight = Math.round(containerHeight);

            this.imageSource = reader.result;
            $(`#${this.modalId}`).addClass('is-active');
          };
          img.src = reader.result;
        },
        false
      );
      reader.readAsDataURL(file);
    },

    /**
     * モーダルを閉じる
     */
    closeModal() {
      $(`#${this.modalId}`).removeClass('is-active');
      this.imageSource = null;
      this.cropperReady = false;
    },

    /**
     * トリミングをキャンセル
     */
    cancelCrop() {
      // 親コンポーネントにキャンセルイベントを発火
      this.$emit('cancelled');
      this.closeModal();
    },

    /**
     * Cropperの準備完了時
     */
    onCropperReady() {
      this.cropperReady = true;

      // cropboxを中央に配置し、指定されたアスペクト比に合わせる
      const cropper = this.$refs.cropper;
      if (cropper) {
        const canvasData = cropper.getCanvasData();
        let cropBoxWidth, cropBoxHeight;

        // アスペクト比に基づいてクロップボックスのサイズを計算
        if (this.aspectRatio >= 1) {
          // 横長または正方形：幅を基準に高さを計算
          cropBoxWidth = Math.min(canvasData.width, canvasData.height * this.aspectRatio);
          cropBoxHeight = cropBoxWidth / this.aspectRatio;
        } else {
          // 縦長：高さを基準に幅を計算
          cropBoxHeight = Math.min(canvasData.height, canvasData.width / this.aspectRatio);
          cropBoxWidth = cropBoxHeight * this.aspectRatio;
        }

        cropper.setCropBoxData({
          width: cropBoxWidth,
          height: cropBoxHeight,
          left: canvasData.left + (canvasData.width - cropBoxWidth) / 2,
          top: canvasData.top + (canvasData.height - cropBoxHeight) / 2,
        });
      }
    },

    /**
     * トリミング確定
     */
    async confirmCrop() {
      if (!this.cropperReady || this.isProcessing) {
        return;
      }

      this.isProcessing = true;
      this.compressionError = null;

      try {
        // Canvasを取得（指定されたサイズで出力）
        const canvas = this.$refs.cropper.getCroppedCanvas({
          width: this.outputWidth,
          height: this.outputHeight,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
        });

        // 段階的に品質を下げて目標サイズ以下に圧縮
        const compressedBlob = await this.compressToTargetSize(
          canvas,
          this.maxFileSize,
          this.preferredFormat
        );

        if (!compressedBlob) {
          const maxSizeKB = Math.round(this.maxFileSize / 1024);
          this.compressionError =
            `画像を${maxSizeKB}KB以下に圧縮できませんでした。別の画像をお試しください。`;
          this.isProcessing = false;
          return;
        }

        this.compressedFileSize = compressedBlob.size;

        // BlobをFileオブジェクトに変換（実際のMIMEタイプに基づいて拡張子を決定）
        const extension = compressedBlob.type === 'image/jpeg' ? 'jpg' : 'png';
        const croppedFile = new File(
          [compressedBlob],
          `cropped-icon.${extension}`,
          { type: compressedBlob.type }
        );

        // 親コンポーネントにイベント発火
        this.$emit('cropped', croppedFile);
        this.closeModal();
      } catch (error) {
        console.error('Cropping error:', error);
        this.compressionError = 'トリミング処理中にエラーが発生しました。';
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Canvas画像を目標サイズ以下に圧縮
     * @param {HTMLCanvasElement} canvas - 圧縮するCanvas
     * @param {number} targetSize - 目標サイズ（バイト）
     * @param {string} preferredFormat - 優先形式（'jpeg' or 'png'）
     * @returns {Promise<Blob|null>} 圧縮されたBlob
     */
    async compressToTargetSize(canvas, targetSize, preferredFormat = 'jpeg') {
      // 優先形式がPNGの場合、まずPNGで試す
      if (preferredFormat === 'png') {
        let blob = await this.canvasToBlob(canvas, 'image/png');
        if (blob.size <= targetSize) {
          return blob;
        }
      }

      // JPEG品質を細かく調整（より高品質から開始）
      const qualities = [1.0, 0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.65, 0.6, 0.55, 0.5, 0.45, 0.4];

      for (const quality of qualities) {
        const blob = await this.canvasToBlob(canvas, 'image/jpeg', quality);
        if (blob.size <= targetSize) {
          return blob;
        }
      }

      // 優先形式がJPEGで、まだPNGを試していない場合は試す
      if (preferredFormat === 'jpeg') {
        const blob = await this.canvasToBlob(canvas, 'image/png');
        if (blob.size <= targetSize) {
          return blob;
        }
      }

      // それでも大きい場合、サイズを緩やかに縮小して再試行
      const scales = [0.95, 0.9, 0.85, 0.8, 0.75, 0.7];
      for (const scale of scales) {
        const scaledWidth = Math.round(this.outputWidth * scale);
        const scaledHeight = Math.round(this.outputHeight * scale);
        const scaledCanvas = this.resizeCanvas(canvas, scaledWidth, scaledHeight);

        // 優先形式がPNGの場合、スケール後もまずPNGで試す
        if (preferredFormat === 'png') {
          const blob = await this.canvasToBlob(scaledCanvas, 'image/png');
          if (blob.size <= targetSize) {
            return blob;
          }
        }

        // PNGでダメな場合、またはJPEG優先の場合はJPEG品質調整を試行
        for (const quality of qualities) {
          const blob = await this.canvasToBlob(scaledCanvas, 'image/jpeg', quality);
          if (blob.size <= targetSize) {
            return blob;
          }
        }
      }

      return null;
    },

    /**
     * CanvasをBlobに変換
     * @param {HTMLCanvasElement} canvas - 変換するCanvas
     * @param {string} mimeType - MIMEタイプ
     * @param {number} quality - 品質（0-1）
     * @returns {Promise<Blob>} Blob
     */
    canvasToBlob(canvas, mimeType, quality = 1) {
      return new Promise((resolve) => {
        canvas.toBlob(resolve, mimeType, quality);
      });
    },

    /**
     * Canvasを指定サイズにリサイズ
     * @param {HTMLCanvasElement} sourceCanvas - 元のCanvas
     * @param {number} width - 新しい幅
     * @param {number} height - 新しい高さ
     * @returns {HTMLCanvasElement} リサイズされたCanvas
     */
    resizeCanvas(sourceCanvas, width, height) {
      const resizedCanvas = document.createElement('canvas');
      resizedCanvas.width = width;
      resizedCanvas.height = height;
      const ctx = resizedCanvas.getContext('2d');

      // 高品質なリサイズのための設定
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(sourceCanvas, 0, 0, width, height);
      return resizedCanvas;
    },

    /**
     * ファイルサイズをフォーマット
     * @param {number} bytes - バイト数
     * @returns {string} フォーマットされた文字列
     */
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      if (bytes < k) return bytes + ' Bytes';
      return (bytes / k).toFixed(2) + ' KB';
    },
  },
};
</script>

<style scoped>
.c-modal__content--large {
  max-width: 600px;
  width: auto;
  max-height: 90vh !important;
  overflow-y: auto !important;
}

.c-modal__title--bold {
  font-weight: bold;
}

.c-modal__description {
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 15px;
}

.c-image-cropper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.c-image-cropper__canvas {
  margin: 0 auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  position: relative;
  z-index: 10;
  background-color: #fff;
}

.c-image-cropper__canvas :deep(.cropper-container) {
  position: relative;
  z-index: 10;
}

.c-image-cropper__processing {
  text-align: center;
  color: #007bff;
  font-weight: bold;
  padding: 10px;
}

.c-image-cropper__error {
  padding: 10px;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  font-size: 14px;
}
</style>

